import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useCurrentUser, useUpdateName } from '@/shared/hooks/api/user'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

function createAccountFormSchema(translate: (key: string) => string) {
  return z.object({
    firstName: z
      .string()
      .min(1, translate('settingsAccount.validation.firstNameRequired'))
      .max(30, translate('settingsAccount.validation.firstNameMax')),
    lastName: z
      .string()
      .min(1, translate('settingsAccount.validation.lastNameRequired'))
      .max(30, translate('settingsAccount.validation.lastNameMax')),
  })
}

type AccountFormValues = z.infer<ReturnType<typeof createAccountFormSchema>>

export function AccountForm() {
  const { t } = useTranslation('common')
  const { data: user, isLoading: isUserLoading } = useCurrentUser()
  const { mutateAsync: updateName, isPending: isUpdating } = useUpdateName()
  const accountFormSchema = useMemo(() => createAccountFormSchema(t), [t])

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
    },
  })

  useEffect(() => {
    if (user) {
      form.reset({
        firstName: user.firstName,
        lastName: user.lastName,
      })
    }
  }, [user, form])

  async function onSubmit(data: AccountFormValues) {
    await updateName({ firstName: data.firstName, lastName: data.lastName })
    toast.success(t('settingsAccount.updatedSuccess'))
  }

  const isDisabled = isUserLoading || isUpdating

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
        <FormField
          control={form.control}
          name='firstName'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsAccount.firstNameLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsAccount.firstNamePlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='lastName'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsAccount.lastNameLabel')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('settingsAccount.lastNamePlaceholder')}
                  {...field}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormDescription>
                {t('settingsAccount.lastNameDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type='submit' disabled={isDisabled}>
          {t('settingsAccount.updateButton')}
        </Button>
      </form>
    </Form>
  )
}
